/**
 * Company queries and mutations for TanStack Query (Vendor App)
 *
 * This module provides all the necessary queries and mutations for company management,
 * adapted for the vendor application's API structure.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet, queryPost, queryPut, queryPatch, queryDelete } from '../utils/request'

// Type definitions
export interface Company {
  id?: number
  razao_social: string
  nome_fantasia?: string
  cnpj: string
  phone?: string
  // Address fields
  street?: string
  number?: string
  complement?: string
  neighborhood?: string
  city?: string
  state?: string
  cep?: string
  codigo_municipio?: string
  // Fiscal/Tax fields
  state_inscription?: string
  municipal_inscription?: string
  company_type?: string
  // Other fields
  tenant_id?: number
  csc?: string
  id_csc?: string
  certificate_uuid?: string
  sefaz_security_key?: string
  cnae?: string
  // Metadata
  is_tenant?: boolean
  admin_emails?: Array<{ email: string }>
  created_at?: string
  updated_at?: string
}

export interface CompanyListParams {
  search?: string
  limit?: number
  offset?: number
  page?: number
  per_page?: number
  [key: string]: any
}

export interface CompanyListResponse {
  items: Company[]
  total?: number
  page?: number
  per_page?: number
}

export interface DeleteResponse {
  success: boolean
  message?: string
}

/**
 * Utility function to prepare company data for API requests
 * Removes empty fields that shouldn't be sent to the API
 */
const discardEmptyFields = (obj: Record<string, any>, fields: string[]): void => {
  for (const field of fields) {
    if (!obj[field]) {
      delete obj[field]
    }
  }
}

const prepare = (company: Company): Company => {
  const payload = { ...company }

  discardEmptyFields(payload, [
    'csc',
    'id_csc',
    'certificate_uuid',
    'nome_fantasia',
    'cnae',
    'municipal_inscription',
    'state_inscription',
    'sefaz_security_key',
    'codigo_municipio',
  ])

  return payload
}

// Cache configuration
const CACHE_TIMES = {
  COMPANIES: 5 * 60 * 1000, // 5 minutes
  COMPANY_DETAIL: 2 * 60 * 1000, // 2 minutes
  ALERTS: 1 * 60 * 1000, // 1 minute
  COMPANY_GROUP: 2 * 60 * 1000, // 2 minutes
  BRANCHES: 2 * 60 * 1000, // 2 minutes
}

/**
 * QUERIES
 */

/**
 * Query hook to fetch a list of companies
 */
export const useCompaniesQuery = (params: MaybeRefOrGetter<CompanyListParams> = {}) => {
  return useQuery({
    queryKey: computed(() => queryKeys.companyList(toValue(params))),
    queryFn: async (): Promise<CompanyListResponse> => {
      const queryParams = toValue(params)
      return queryGet<CompanyListResponse>('/v2/companies/', { params: queryParams })
    },
    staleTime: CACHE_TIMES.COMPANIES,
    enabled: true,
  })
}

/**
 * Query hook to fetch a single company by ID
 */
export const useCompanyQuery = (companyId: MaybeRefOrGetter<number | null | undefined>) => {
  return useQuery({
    queryKey: computed(() => {
      const id = toValue(companyId)
      return id ? queryKeys.company(id) : ['companies', 'null']
    }),
    queryFn: async (): Promise<Company> => {
      const id = toValue(companyId)
      if (!id) throw new Error('Company ID is required')
      return queryGet<Company>(`/v2/companies/${id}`)
    },
    staleTime: CACHE_TIMES.COMPANY_DETAIL,
    enabled: computed(() => !!toValue(companyId)),
  })
}

/**
 * Query hook to fetch company alerts for the current company
 */
export const useCompanyAlertsQuery = (companyId: MaybeRefOrGetter<number | null | undefined>) => {
  return useQuery({
    queryKey: computed(() => {
      const id = toValue(companyId)
      return id ? [...queryKeys.company(id), 'alerts'] : ['companies', 'alerts', 'null']
    }),
    queryFn: async (): Promise<any> => {
      const id = toValue(companyId)
      if (!id) throw new Error('Company ID is required')
      return queryGet<any>(`/v2/companies/alerts/${id}`)
    },
    staleTime: CACHE_TIMES.ALERTS,
    enabled: computed(() => !!toValue(companyId)),
  })
}

/**
 * Query hook to fetch company group
 */
export const useCompanyGroupQuery = (companyId: MaybeRefOrGetter<number | null | undefined>) => {
  return useQuery({
    queryKey: computed(() => {
      const id = toValue(companyId)
      return id ? [...queryKeys.company(id), 'group'] : ['companies', 'group', 'null']
    }),
    queryFn: async (): Promise<any> => {
      const id = toValue(companyId)
      if (!id) throw new Error('Company ID is required')
      return queryGet<any>(`/v2/companies/company-group/${id}`)
    },
    staleTime: CACHE_TIMES.COMPANY_GROUP,
    enabled: computed(() => !!toValue(companyId)),
  })
}

/**
 * Query hook to fetch company branches
 */
export const useCompanyBranchesQuery = (companyId: MaybeRefOrGetter<number | null | undefined>) => {
  return useQuery({
    queryKey: computed(() => {
      const id = toValue(companyId)
      return id ? [...queryKeys.company(id), 'branches'] : ['companies', 'branches', 'null']
    }),
    queryFn: async (): Promise<any[]> => {
      const id = toValue(companyId)
      if (!id) throw new Error('Company ID is required')
      return queryGet<any[]>(`/v2/companies/${id}/branches`)
    },
    staleTime: CACHE_TIMES.BRANCHES,
    enabled: computed(() => !!toValue(companyId)),
  })
}

/**
 * MUTATIONS
 */

/**
 * Mutation hook to create a new company
 */
export const useCreateCompanyMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { company: Company; tenantId?: number }): Promise<any> => {
      const params = data.tenantId ? { tenant_id: data.tenantId } : {}
      return queryPost<any>('/v2/companies/', prepare(data.company), { params })
    },
    onSuccess: () => {
      // Invalidate companies list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.companies() })
    },
  })
}

/**
 * Mutation hook to update an existing company
 */
export const useUpdateCompanyMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (company: Company): Promise<Company> => {
      if (!company.id) throw new Error('Company ID is required for update')
      return queryPatch<Company>(`/v2/companies/${company.id}`, prepare(company))
    },
    onSuccess: (updatedCompany) => {
      // Invalidate both the list and the individual company
      queryClient.invalidateQueries({ queryKey: queryKeys.companies() })
      if (updatedCompany.id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.company(updatedCompany.id) })
      }
    },
  })
}

/**
 * Mutation hook to delete a company
 */
export const useDeleteCompanyMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (companyId: number): Promise<DeleteResponse> => {
      return queryDelete<DeleteResponse>(`/v2/companies/${companyId}`)
    },
    onSuccess: (_, companyId) => {
      // Remove the individual company query and invalidate the list
      queryClient.removeQueries({ queryKey: queryKeys.company(companyId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.companies() })
    },
  })
}

/**
 * Hook that combines company queries and mutations for convenient usage
 */
export const useCompanyOperations = () => {
  return {
    // Queries
    useCompaniesQuery,
    useCompanyQuery,
    useCompanyAlertsQuery,
    useCompanyGroupQuery,
    useCompanyBranchesQuery,

    // Mutations
    useCreateCompanyMutation,
    useUpdateCompanyMutation,
    useDeleteCompanyMutation,
  }
}

// Export individual functions for tree-shaking
export {
  useCompaniesQuery as useListCompaniesQuery,
  useCompanyQuery as useGetCompanyQuery,
}
